<!--
  VoucherSummaryTab — 바우처관리 › 현황
  · GET /voucher/org/{org}/summary (현황 카드) + GET /voucher/org/{org}/stats/classrooms (클래스별 사용 통계)
  · balance_shortfall > 0 이면 "잔액 부족 예상" 경고
-->
<template>
  <div>
    <div v-if="loading && !summary" class="p-loader"><div class="p-spinner" /></div>
    <template v-else-if="summary">
      <div class="p-grid p-grid--kpi">
        <a-card padding="20">
          <a-stat
            label="바우처 잔액"
            :value="fmtAmount(summary.voucher_balance)"
            :unit="summary.voucher_currency || ''"
            :delta="`기관 할인 ${summary.voucher_discount_rate == null ? '-' : summary.voucher_discount_rate}%`"
            delta-kind="info" delta-suffix="" :delta-icon="false"
          />
        </a-card>
        <a-card padding="20">
          <a-stat
            label="이번 달 등록"
            :value="String(summary.month_registered_count || 0)"
            unit="건"
            :delta="`차감 ${fmtAmount(summary.month_registered_amount, summary.voucher_currency)}`"
            delta-kind="info" delta-suffix="" :delta-icon="false"
          />
        </a-card>
        <a-card padding="20">
          <a-stat
            label="진행 중 지정"
            :value="String(summary.active_assignment_count || 0)"
            unit="건"
            :delta="`누적 등록 ${summary.total_registered_count || 0}건`"
            delta-kind="info" delta-suffix="" :delta-icon="false"
          />
        </a-card>
        <a-card padding="20">
          <a-stat
            label="등록 대기 예상액"
            :value="fmtAmount(summary.pending_estimate)"
            :unit="summary.voucher_currency || ''"
            :delta="shortfall ? `부족 예상 ${fmtAmount(summary.balance_shortfall, summary.voucher_currency)}` : '잔액 충분'"
            :delta-kind="shortfall ? 'err' : 'ok'" delta-suffix="" :delta-icon="false"
          />
        </a-card>
      </div>

      <div v-if="summary.voucher_currency && summary.voucher_currency !== 'USD'" class="p-alert p-alert--info p-shortfall">
        <div class="p-alert__body">바우처 통화가 <strong>{{ summary.voucher_currency }}</strong> 입니다. 도서 가격을 바우처 통화로 환산해 차감하므로 <strong>차감 금액이 환율에 따라 달라집니다.</strong></div>
      </div>

      <div v-if="shortfall" class="p-alert p-alert--warn p-shortfall">
        <div class="p-alert__title">잔액 부족 예상</div>
        <div class="p-alert__body">
          진행 중 지정의 등록 대기 예상액이 잔액을 <strong>{{ fmtAmount(summary.balance_shortfall, summary.voucher_currency) }}</strong> 초과합니다.
          학생 등록이 막히기 전에 바우처 충전을 요청하세요.
          <a-btn size="sm" kind="primary" class="p-ml-8" @click="$emit('go-tab', 'purchase')">충전 요청으로</a-btn>
        </div>
      </div>

      <div class="p-total-line">
        <span v-if="summary.requested_purchase_count">요청 중 충전 <strong>{{ summary.requested_purchase_count }}</strong>건</span>
        <span v-if="summary.last_purchase">
          마지막 확정 충전 <strong>{{ fmtAmount(summary.last_purchase.credited_amount, summary.last_purchase.voucher_currency) }}</strong>
          · 확정 {{ fmtDateTime(summary.last_purchase.confirmed_at) }}<template v-if="summary.last_purchase.deposited_on"> · 입금일 {{ fmtDate(summary.last_purchase.deposited_on) }}</template>
        </span>
        <span v-else>확정된 충전이 아직 없습니다</span>
        <span v-if="summary.active_yn && summary.active_yn !== 'Y'" class="p-warn-text">기관 비활성 상태</span>
      </div>
    </template>
    <div v-else-if="summaryError" class="p-alert p-alert--err"><div class="p-alert__body">{{ summaryError }}</div></div>

    <a-card title="클래스별 사용 통계" sub="기간 필터는 등록일에만 걸리고, 수강생·미등록 인원은 현재 값입니다" overflow="visible">
      <div class="p-filters">
        <span class="p-filters__label">등록일</span>
        <input v-model="filters.date_from" type="date" class="p-input" />
        <span class="p-filters__label">~</span>
        <input v-model="filters.date_to" type="date" class="p-input" />
        <select v-model="filters.class_status" class="p-input p-select">
          <option value="ongoing">진행 중 클래스</option>
          <option value="closed">종료 클래스</option>
          <option value="cancelled">취소 클래스</option>
          <option value="all">전체</option>
        </select>
        <a-btn kind="primary" size="sm" :disabled="statsLoading" @click="loadStats">조회</a-btn>
        <a-btn kind="ghost" size="sm" @click="resetFilters">초기화</a-btn>
      </div>

      <div v-if="statsLoading" class="p-loader"><div class="p-spinner" /></div>
      <div v-else-if="statsError" class="p-alert p-alert--err"><div class="p-alert__body">{{ statsError }}</div></div>
      <template v-else-if="stats">
        <div v-if="stats.totals" class="p-total-line">
          <span>합계 · 수강생 <strong>{{ stats.totals.student_count }}</strong>명</span>
          <span>등록 <strong>{{ stats.totals.registered_count }}</strong> · 미등록 <strong>{{ stats.totals.unregistered_count }}</strong></span>
          <span>등록률 <strong>{{ fmtRate(stats.totals.registration_rate) }}</strong></span>
          <span>차감 <strong>{{ fmtAmount(stats.totals.registered_amount, stats.currency_code) }}</strong></span>
          <span>예상 <strong>{{ fmtAmount(stats.totals.pending_estimate, stats.currency_code) }}</strong></span>
        </div>
        <a-empty v-if="!stats.items || !stats.items.length" title="집계할 클래스가 없습니다" />
        <a-table v-else :columns="cols" :rows="stats.items" row-key="classroom_idx">
          <template #cell-name="{ row }">
            <div class="p-cell-class is-link" @click="openDetail(row)">
              <div class="p-avatar">{{ (row.name || 'C').charAt(0) }}</div>
              <div>
                <div class="p-cell-title">{{ row.name }}</div>
                <div class="p-cell-sub">{{ row.instructor_name || '-' }} · {{ fmtDate(row.start_date) }} ~ {{ fmtDate(row.end_date) }}</div>
              </div>
            </div>
          </template>
          <template #cell-class_status="{ value }"><a-pill :kind="classStatusKind(value)">{{ classStatusLabel(value) }}</a-pill></template>
          <template #cell-student_count="{ value }"><span class="p-mono">{{ value }}</span></template>
          <template #cell-assignment_count="{ value }"><span class="p-mono">{{ value }}</span></template>
          <template #cell-registered="{ row }"><span class="p-mono">{{ row.registered_count }} / {{ row.unregistered_count }}</span></template>
          <template #cell-registration_rate="{ value }">
            <div class="p-progress">
              <div class="p-progress__bar"><div class="p-progress__fill" :style="{ width: Math.min(100, Number(value) || 0) + '%' }" /></div>
              <span class="p-progress__num">{{ fmtRate(value) }}</span>
            </div>
          </template>
          <template #cell-registered_amount="{ value }"><span class="p-amount">{{ fmtAmount(value) }}</span></template>
          <template #cell-pending_estimate="{ value }"><span class="p-mono">{{ fmtAmount(value) }}</span></template>
          <template #cell-actions="{ row }">
            <div class="p-row-actions">
              <a-btn size="sm" kind="ghost" @click="openDetail(row)">상세</a-btn>
              <a-btn size="sm" kind="ghost" @click="goClass(row)">클래스</a-btn>
            </div>
          </template>
        </a-table>
      </template>
    </a-card>

    <class-stats-modal
      v-if="detailIdx"
      :org-idx="orgIdx"
      :classroom-idx="detailIdx"
      :date-from="filters.date_from"
      :date-to="filters.date_to"
      @close="detailIdx = null"
    />
  </div>
</template>

<script>
import ACard from '@/components/Pineapple/ACard.vue';
import AStat from '@/components/Pineapple/AStat.vue';
import ATable from '@/components/Pineapple/ATable.vue';
import APill from '@/components/Pineapple/APill.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';
import AEmpty from '@/components/Pineapple/AEmpty.vue';
import ClassStatsModal from './ClassStatsModal.vue';
import { fmtAmount, fmtDate, fmtDateTime, fmtRate, isPositiveAmount, classStatusKind, classStatusLabel, voucherErrorText } from '../orgUtils';

export default {
  name: 'VoucherSummaryTab',
  components: { ACard, AStat, ATable, APill, ABtn, AEmpty, ClassStatsModal },
  props: {
    orgIdx: { type: [Number, String], required: true },
  },
  emits: ['go-tab'],
  data() {
    return {
      summary: null,
      summaryError: '',
      loading: false,
      stats: null,
      statsError: '',
      statsLoading: false,
      filters: { date_from: '', date_to: '', class_status: 'ongoing' },
      detailIdx: null,
      cols: [
        { key: 'name',              label: '클래스' },
        { key: 'class_status',      label: '상태',        width: '80px' },
        { key: 'student_count',     label: '수강생',      width: '70px',  align: 'right' },
        { key: 'assignment_count',  label: '지정',        width: '60px',  align: 'right' },
        { key: 'registered',        label: '등록 / 미등록', width: '110px', align: 'right' },
        { key: 'registration_rate', label: '등록률',      width: '150px' },
        { key: 'registered_amount', label: '차감액',      width: '110px', align: 'right' },
        { key: 'pending_estimate',  label: '예상액',      width: '110px', align: 'right' },
        { key: 'actions',           label: '',            width: '130px', align: 'right' },
      ],
    };
  },
  computed: {
    shortfall() { return !!this.summary && isPositiveAmount(this.summary.balance_shortfall); },
  },
  mounted() {
    this.loadSummary();
    this.loadStats();
  },
  methods: {
    fmtAmount, fmtDate, fmtDateTime, fmtRate, classStatusKind, classStatusLabel,
    async loadSummary() {
      this.loading = true;
      this.summaryError = '';
      try {
        const res = await this.$axios({ method: 'get', url: `/voucher/org/${this.orgIdx}/summary`, loadingBar: false });
        this.summary = res.data || null;
      } catch (e) {
        this.summary = null;
        this.summaryError = voucherErrorText(e, '현황을 불러오지 못했습니다.');
      } finally {
        this.loading = false;
      }
    },
    async loadStats() {
      this.statsLoading = true;
      this.statsError = '';
      try {
        const params = { class_status: this.filters.class_status };
        if (this.filters.date_from) params.date_from = this.filters.date_from;
        if (this.filters.date_to) params.date_to = this.filters.date_to;
        const res = await this.$axios({ method: 'get', url: `/voucher/org/${this.orgIdx}/stats/classrooms`, params, loadingBar: false });
        this.stats = res.data || null;
      } catch (e) {
        this.stats = null;
        this.statsError = voucherErrorText(e, '통계를 불러오지 못했습니다.');
      } finally {
        this.statsLoading = false;
      }
    },
    resetFilters() {
      this.filters = { date_from: '', date_to: '', class_status: 'ongoing' };
      this.loadStats();
    },
    openDetail(row) { this.detailIdx = row.classroom_idx; },
    goClass(row) {
      this.$router.push({ name: 'AcademicClassesDetail', query: { idx: row.classroom_idx, view: 'org' } });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../_org.scss';

.p-shortfall { margin-bottom: 16px; }
.p-ml-8 { margin-left: 8px; }
.p-warn-text { color: $p-err; font-weight: 700; }
</style>
